/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React from 'react';
import { FieldValues, UseFormReturn, useFieldArray } from 'react-hook-form';

/**
 * Generic editor for `projection_rate_schedule`. Works for any form whose
 * values include the shared `rateModeFields` (family-savings, APV, ...).
 * The field path is cast to `never` because a generic `T` can't statically
 * prove it owns the key — every host schema guarantees it via `rateModeFields`.
 */
export function renderRateScheduleField<T extends FieldValues>(
  form: UseFormReturn<T>,
) {
  return <RateScheduleEditor form={form} />;
}

interface Props<T extends FieldValues> {
  form: UseFormReturn<T>;
}

function RateScheduleEditor<T extends FieldValues>({ form }: Props<T>) {
  const { control, register, formState: { errors } } = form;
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'projection_rate_schedule' as never,
  });

  const lastIndex = fields.length - 1;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  /* eslint-disable @typescript-eslint/no-unsafe-member-access */
  const tierErrors = (errors as any).projection_rate_schedule as
    | Array<{ annual_rate?: { message?: string }; until?: { message?: string } } | undefined>
    | undefined;
  const rootError = (errors as any).projection_rate_schedule?.message as string | undefined;
  /* eslint-enable @typescript-eslint/no-explicit-any */
  /* eslint-enable @typescript-eslint/no-unsafe-member-access */

  return (
    <div className="border rounded p-2">
      {fields.length === 0 && (
        <p className="text-muted small mb-2">No hay tramos definidos.</p>
      )}

      {fields.map((field, index) => {
        const isLast = index === lastIndex;
        const tierError = tierErrors?.[index];
        const rateError = tierError?.annual_rate?.message;
        const untilError = tierError?.until?.message;
        return (
          <div key={field.id} className="row g-2 align-items-end mb-2">
            <div className="col-md-4">
              <label className="form-label small mb-1">Tasa anual</label>
              <input
                type="number"
                step="0.0001"
                className={`form-control form-control-sm ${rateError ? 'is-invalid' : ''}`}
                onWheel={(e) => e.currentTarget.blur()}
                {...register(
                  `projection_rate_schedule.${index}.annual_rate` as never,
                )}
              />
              {rateError && (
                <div className="invalid-feedback d-block">{rateError}</div>
              )}
            </div>
            <div className="col-md-5">
              <label className="form-label small mb-1">
                Hasta (YYYY-MM, inclusive)
                {isLast && ' — vacío = para siempre'}
              </label>
              <input
                type="month"
                className={`form-control form-control-sm ${untilError ? 'is-invalid' : ''}`}
                {...register(
                  `projection_rate_schedule.${index}.until` as never,
                  { setValueAs: (v: unknown) => (v === '' ? null : v) },
                )}
              />
              {untilError && (
                <div className="invalid-feedback d-block">{untilError}</div>
              )}
            </div>
            <div className="col-md-3 d-flex gap-1">
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => remove(index)}
                disabled={fields.length <= 1}
              >
                Quitar
              </button>
            </div>
          </div>
        );
      })}

      {rootError && (
        <div className="text-danger small mb-2">{rootError}</div>
      )}

      <button
        type="button"
        className="btn btn-sm btn-outline-primary"
        onClick={() => append({ annual_rate: 0.02, until: null } as never)}
      >
        + Agregar tramo
      </button>
    </div>
  );
}
